import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useEventStore } from '@/state/stores/use-event-store.ts';
import useAddSessionContributor from '@/state/mutations/sessions/add-session-contributor.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { useNotifications } from '@/hooks/use-notifications';
import useCreateContributorMutation from '@/state/mutations/contributors/use-create-contributor.ts';
import { ContributorAutocomplete, type ActorOption } from '../../common/contributor-autocomplete';
import { useSocketStore } from '@/state/stores/use-socket-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useGetSessionQuery, GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { getAvailableSessionContributors } from '@/utils/contributor-extraction.utils';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { getObjectId, resolveRefId } from '@/helpers/object.id.helper';
import type { Mode } from '@/types/entity-mode';
import { ServerError } from '@/types/errors/server.error';
import { resolveContributorAnchorFlowSlotId } from '@/components/flows/detail/slot-list/session-segment.helpers';
import { settleContributorAddVisibility } from '@/helpers/settle-contributor-add-visibility.helper';

function resolveSessionOriginId(session: SessionDto): string {
  if (session.origin) {
    return getObjectId(session.origin);
  }
  return session.id;
}

function resolveEventIdForSession(
  session: SessionDto,
  eventFromStore?: { id: string; origin?: unknown },
): string | undefined {
  if (session.parent) {
    return getObjectId(session.parent);
  }
  if (!eventFromStore) return undefined;
  const originId = resolveRefId(eventFromStore.origin as Parameters<typeof resolveRefId>[0]);
  return originId ?? eventFromStore.id;
}

function toBackendMode(mode: Mode): Mode | undefined {
  if (mode === 'PLAN') return 'PLAN';
  if (mode === 'ORGANIZE' || mode === 'PERFORM') return 'ORGANIZE';
  return undefined;
}

export const AddSessionContributorList = ({
  session,
  onSuccess,
  modeOverride,
  enableFlowSlotContext = false,
}: {
  session: SessionDto;
  onSuccess: () => void;
  modeOverride?: Mode;
  enableFlowSlotContext?: boolean;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const contributors = useEventStore((s) => s.contributors);
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const socket = useSocketStore((s) => s.socket);
  const { setFlow, flow, selectedSlot } = useFlowStore();
  const queryClient = useQueryClient();
  const eventMode = useEventMode();
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');

  const mode = modeOverride ?? eventMode;
  const backendMode = toBackendMode(mode);
  const sessionOriginId = useMemo(() => resolveSessionOriginId(session), [session]);

  const organizeSessionFromStore = useMemo(() => {
    if (!sessionOriginId) return undefined;
    return eventSessionsOrganize?.find((s) => {
      const originId = s.origin ? getObjectId(s.origin) : s.id;
      return originId === sessionOriginId || s.id === sessionOriginId;
    });
  }, [eventSessionsOrganize, sessionOriginId]);

  const { data: planSession, error: planSessionError } = useGetSessionQuery(sessionOriginId, 'PLAN');
  const planNotFound =
    backendMode === 'PLAN' && planSessionError instanceof ServerError && planSessionError.statusCode === 404;
  const shouldFetchOrganize =
    !!sessionOriginId && (backendMode === 'ORGANIZE' || (planNotFound && !!organizeSessionFromStore));
  const { data: organizeSession } = useGetSessionQuery(sessionOriginId, 'ORGANIZE', {
    enabled: shouldFetchOrganize,
  });

  const freshSession = useMemo(() => {
    if (backendMode === 'PLAN') {
      return planSession ?? organizeSession;
    }
    return organizeSession ?? planSession;
  }, [backendMode, organizeSession, planSession]);

  const currentSession = freshSession ?? session;
  const eventId = useMemo(
    () => resolveEventIdForSession(session, event ?? eventOrganize ?? eventPlan),
    [session, event, eventOrganize, eventPlan],
  );

  const flowSlotContext = useMemo(() => {
    if (!enableFlowSlotContext || !selectedSlot || selectedSlot.contentType !== 'Session' || !flow) {
      return null;
    }
    const flowSlotId = resolveContributorAnchorFlowSlotId(flow, selectedSlot);
    const eventSlotId =
      eventSlotIdParam ??
      (flow.slot?.id ? getObjectId(flow.slot.id) : undefined) ??
      (flow.slot?.origin ? getObjectId(flow.slot.origin) : undefined);
    if (!eventSlotId) {
      return { flowSlotId, useFromFlowContext: false as const };
    }
    return { flowSlotId, eventSlotId, useFromFlowContext: true as const };
  }, [enableFlowSlotContext, selectedSlot, flow, eventSlotIdParam]);

  const actors = useMemo<ActorOption[]>(() => {
    if (!contributors?.length) return [];
    const availableContributors = getAvailableSessionContributors(currentSession, contributors);
    return availableContributors.map((contributor) => ({
      id: contributor.id,
      name: contributor.profiles[0]?.displayName || `Contributor ${contributor.id.slice(-6)}`,
    }));
  }, [contributors, currentSession]);

  const { mutateAsync: addSessionContributorMutation, isPending: isAddContributorPending } = useAddSessionContributor();
  const { mutateAsync: createContributorMutation, isPending: isCreateContributorPending } =
    useCreateContributorMutation();
  const [isSettling, setIsSettling] = useState(false);

  const buildAddContributorPayload = (contributorId: string) => {
    const payload = {
      sessionId: currentSession.id,
      contributorId,
    };
    if (flowSlotContext?.useFromFlowContext) {
      return {
        ...payload,
        fromFlowContext: true,
        flowSlotId: flowSlotContext.flowSlotId,
        eventSlotId: flowSlotContext.eventSlotId,
      };
    }
    return payload;
  };

  const settleAfterAdd = async () => {
    const extraQueryKeys: (readonly unknown[])[] = [[GET_SESSION_QUERY_KEY, currentSession.id]];
    if (sessionOriginId && sessionOriginId !== currentSession.id) {
      extraQueryKeys.push([GET_SESSION_QUERY_KEY, sessionOriginId]);
    }
    if (flow?.id) {
      extraQueryKeys.push([GET_FLOW_QUERY_KEY, flow.id]);
    }
    await settleContributorAddVisibility(queryClient, {
      eventId: eventId,
      extraQueryKeys,
    });
  };

  const addSessionContributor = async (actor: ActorOption) => {
    if (!contributors) return;
    setIsSettling(true);
    try {
      await addSessionContributorMutation(buildAddContributorPayload(actor.id));

      notification.notifySuccess(t('session.notification.addContributorSuccess', { name: actor.name }));
      socket?.emit('flowGet', { flowId: flow?.id }, (updatedFlow: FlowDto) => {
        setFlow(updatedFlow);
      });
      await settleAfterAdd();
      onSuccess();
    } catch (error) {
      console.error(error);
      notification.notifyError(t('session.notification.addContributorError'));
    } finally {
      setIsSettling(false);
    }
  };

  const createContributor = async (name: string) => {
    if (!eventId) {
      notification.notifyError(t('session.notification.addContributorError'));
      return;
    }
    setIsSettling(true);
    try {
      const newContributor = await createContributorMutation({
        event: eventId,
        profiles: [{ displayName: name, editor: 'Event' }],
        roles: [],
      });

      await queryClient.invalidateQueries({
        queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId],
      });

      await addSessionContributorMutation(buildAddContributorPayload(newContributor.id));

      socket?.emit('flowGet', { flowId: flow?.id }, (updatedFlow: FlowDto) => {
        setFlow(updatedFlow);
      });

      notification.notifySuccess(t('session.notification.addContributorSuccess', { name }));
      await settleAfterAdd();
      onSuccess();
    } catch (error) {
      console.error(error);
      notification.notifyError(t('session.notification.addContributorError'));
    } finally {
      setIsSettling(false);
    }
  };

  const isPending = useMemo(() => {
    return isAddContributorPending || isCreateContributorPending || isSettling;
  }, [isAddContributorPending, isCreateContributorPending, isSettling]);

  return (
    <ContributorAutocomplete
      options={actors}
      loading={isPending}
      labelKey="session.addActorLabel"
      autocompleteId="add-session-actor-autocomplete"
      disabled={isPending}
      onSelectExisting={addSessionContributor}
      onCreateNew={createContributor}
    />
  );
};
